/**
 * NavigatorX Billing Service
 * 
 * Service for managing subscriptions and billing via RevenueCat integration.
 * Handles:
 * - RevenueCat API integration
 * - Subscription status sync
 * - Transaction history
 * - Refunds and cancellations
 * - Billing analytics
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { navDb, functions } from '../firebase/client';
import { httpsCallable } from 'firebase/functions';
import { logAdminAction } from './navigatorXAuditService';
import { userDocRef, NAV_USERS_COLLECTION } from './navigatorXService';
import type { NavUser, NavUserSubscriptionStatus } from '../../types/navigatorx';

// ============================================================================
// TYPES
// ============================================================================

export interface RevenueCatCustomer {
  id: string;
  originalAppUserId: string;
  firstSeen: string;
  lastSeen: string;
  subscriberAttributes: Record<string, { value: string; updatedAtMs: number }>;
  subscriptions: Record<string, RevenueCatSubscription>;
  nonSubscriptions: Record<string, RevenueCatPurchase[]>;
  entitlements: Record<string, RevenueCatEntitlement>;
}

export interface RevenueCatSubscription {
  productIdentifier: string;
  purchaseDate: string;
  originalPurchaseDate: string;
  expiresDate: string | null;
  store: 'app_store' | 'play_store' | 'stripe' | 'promotional';
  isSandbox: boolean;
  unsubscribeDetectedAt: string | null;
  billingIssuesDetectedAt: string | null;
  gracePeriodExpiresDate: string | null;
  autoResumeDate: string | null;
  ownershipType: 'PURCHASED' | 'FAMILY_SHARED';
  periodType: 'normal' | 'trial' | 'intro';
  refundedAt: string | null;
}

export interface RevenueCatPurchase {
  id: string;
  purchaseDate: string;
  store: string;
  isSandbox: boolean;
}

export interface RevenueCatEntitlement {
  identifier: string;
  expiresDate: string | null;
  purchaseDate: string;
  originalPurchaseDate: string;
  productIdentifier: string;
  isSandbox: boolean;
  willRenew: boolean;
  periodType: string;
  store: string;
  ownershipType: string;
}

export interface Transaction {
  id: string;
  userId: string;
  type: 'purchase' | 'renewal' | 'refund' | 'cancellation' | 'trial_start' | 'trial_convert' | 'admin_grant';
  amount: number;
  currency: string;
  productId: string;
  store: string;
  status: 'completed' | 'pending' | 'refunded' | 'failed';
  timestamp: Timestamp | string;
  revenueCatId?: string;
  adminId?: string;
  notes?: string;
}

export interface BillingStats {
  mrr: number;
  arr: number;
  totalRevenue: number;
  totalSubscribers: number;
  activeTrials: number;
  churnRate: number;
  conversionRate: number;
  ltv: number;
  averageRevenuePerUser: number;
  revenueGrowth: number;
  failedPayments: number;
}

export interface PromoCode {
  id: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed' | 'free_months';
  discountValue: number;
  maxUses: number | null;
  usedCount: number;
  validFrom: Timestamp;
  validUntil: Timestamp;
  active: boolean;
  createdAt: Timestamp;
  createdBy: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PROMO_CODES_COLLECTION = 'nav_promo_codes';
const TRANSACTIONS_COLLECTION = 'nav_transactions';

/** NavigatorX subscription price */
export const SUBSCRIPTION_PRICE = 9.99;

/** RevenueCat API endpoints */
const REVENUECAT_API_BASE = 'https://api.revenuecat.com/v1';

// ============================================================================
// REVENUECAT INTEGRATION
// ============================================================================

/**
 * Fetch customer info from RevenueCat
 */
export async function fetchRevenueCatCustomer(userId: string): Promise<RevenueCatCustomer | null> {
  try {
    const fetchCustomer = httpsCallable(functions, 'fetchRevenueCatCustomer');
    const result = await fetchCustomer({ userId });
    return result.data as RevenueCatCustomer;
  } catch (error) {
    console.error('Failed to fetch RevenueCat customer:', error);
    return null;
  }
}

/**
 * Get subscription details for a user
 */
export async function getSubscriptionDetails(userId: string): Promise<{
  customer: RevenueCatCustomer | null;
  activeSubscription: RevenueCatSubscription | null;
  entitlements: RevenueCatEntitlement[];
  isActive: boolean;
  isTrial: boolean;
  expiresAt: Date | null;
}> {
  const customer = await fetchRevenueCatCustomer(userId);
  
  if (!customer) {
    return {
      customer: null,
      activeSubscription: null,
      entitlements: [],
      isActive: false,
      isTrial: false,
      expiresAt: null
    };
  }

  // Find active subscription
  let activeSubscription: RevenueCatSubscription | null = null;
  let isActive = false;
  let isTrial = false;
  let expiresAt: Date | null = null;

  for (const [productId, subscription] of Object.entries(customer.subscriptions)) {
    if (subscription.expiresDate) {
      const expires = new Date(subscription.expiresDate);
      if (expires > new Date()) {
        activeSubscription = subscription;
        isActive = true;
        isTrial = subscription.periodType === 'trial';
        expiresAt = expires;
        break;
      }
    }
  }

  // Get entitlements
  const entitlements = Object.values(customer.entitlements);

  return {
    customer,
    activeSubscription,
    entitlements,
    isActive,
    isTrial,
    expiresAt
  };
}

/**
 * Sync subscription status from RevenueCat to Firestore
 */
export async function syncSubscriptionStatus(
  userId: string,
  adminId?: string
): Promise<NavUserSubscriptionStatus> {
  const { isActive, isTrial, expiresAt, activeSubscription } = await getSubscriptionDetails(userId);
  
  let newStatus: NavUserSubscriptionStatus;
  
  if (!isActive) {
    newStatus = 'SUBSCRIPTION_EXPIRED';
  } else if (isTrial) {
    newStatus = 'TRIAL_ACTIVE';
  } else {
    newStatus = 'SUBSCRIBED';
  }

  const updates: Partial<NavUser> = {
    subscriptionStatus: newStatus,
    lastRevenueCatSync: Timestamp.now(),
    updatedAt: Timestamp.now()
  };

  if (expiresAt) {
    if (isTrial) {
      updates.trialEndDate = Timestamp.fromDate(expiresAt);
    } else {
      updates.subscriptionEndDate = Timestamp.fromDate(expiresAt);
    }
  }

  if (activeSubscription) {
    updates.revenueCatCustomerId = userId;
  }

  await updateDoc(userDocRef(userId), updates);

  if (adminId) {
    await logAdminAction(adminId, 'UPDATE_USER', userId, {
      action: 'sync_subscription',
      newStatus,
      source: 'revenuecat'
    });
  }

  return newStatus;
}

/**
 * Grant promotional subscription via RevenueCat
 */
export async function grantPromotionalSubscription(
  userId: string,
  months: number,
  adminId: string
): Promise<void> {
  try {
    const grantPromo = httpsCallable(functions, 'grantRevenueCatPromo');
    await grantPromo({
      userId,
      duration: months,
      entitlement: 'premium'
    });

    // Update local status
    const endDate = new Date();
    endDate.setMonth(endDate.getMonth() + months);

    await updateDoc(userDocRef(userId), {
      subscriptionStatus: 'SUBSCRIBED',
      subscriptionStartDate: Timestamp.now(),
      subscriptionEndDate: Timestamp.fromDate(endDate),
      grantedByAdmin: adminId,
      grantedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    // Record transaction
    await recordTransaction({
      userId,
      type: 'admin_grant',
      amount: 0,
      currency: 'USD',
      productId: `promo_${months}mo`,
      store: 'promotional',
      status: 'completed',
      adminId,
      notes: `${months} month promotional subscription granted by admin`
    });

    await logAdminAction(adminId, 'GRANT_SUBSCRIPTION', userId, {
      months,
      type: 'promotional',
      endDate: endDate.toISOString()
    });
  } catch (error) {
    console.error('Failed to grant promotional subscription:', error);
    throw error;
  }
}

/**
 * Request refund via RevenueCat
 */
export async function requestRefund(
  userId: string,
  transactionId: string,
  reason: string,
  adminId: string
): Promise<boolean> {
  try {
    const requestRefund = httpsCallable(functions, 'requestRevenueCatRefund');
    const result = await requestRefund({
      userId,
      transactionId,
      reason
    });

    await logAdminAction(adminId, 'CANCEL_SUBSCRIPTION', userId, {
      action: 'refund_requested',
      transactionId,
      reason
    });

    return (result.data as any).success;
  } catch (error) {
    console.error('Failed to request refund:', error);
    throw error;
  }
}

// ============================================================================
// TRANSACTION MANAGEMENT
// ============================================================================

/**
 * Record a transaction
 */
export async function recordTransaction(
  transaction: Omit<Transaction, 'id' | 'timestamp'>
): Promise<string> {
  const docRef = await addDoc(collection(navDb, TRANSACTIONS_COLLECTION), {
    ...transaction,
    timestamp: Timestamp.now()
  });
  return docRef.id;
}

/**
 * Get transactions for a user
 */
export async function getUserTransactions(
  userId: string,
  limitCount: number = 50
): Promise<Transaction[]> {
  const q = query(
    collection(navDb, TRANSACTIONS_COLLECTION),
    where('userId', '==', userId),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Transaction[];
}

/**
 * Get all transactions with filters
 */
export async function getTransactions(options: {
  type?: Transaction['type'];
  status?: Transaction['status'];
  startDate?: Date;
  endDate?: Date;
  limitCount?: number;
} = {}): Promise<Transaction[]> {
  let q = query(
    collection(navDb, TRANSACTIONS_COLLECTION),
    orderBy('timestamp', 'desc'),
    limit(options.limitCount || 100)
  );

  if (options.type) {
    q = query(q, where('type', '==', options.type));
  }

  if (options.status) {
    q = query(q, where('status', '==', options.status));
  }

  const snapshot = await getDocs(q);
  let transactions = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Transaction[];

  // Client-side date filtering
  if (options.startDate || options.endDate) {
    transactions = transactions.filter(t => {
      const date = t.timestamp instanceof Timestamp ? t.timestamp.toDate() : new Date(t.timestamp);
      if (options.startDate && date < options.startDate) return false;
      if (options.endDate && date > options.endDate) return false;
      return true;
    });
  }

  return transactions;
}

// ============================================================================
// BILLING ANALYTICS
// ============================================================================

/**
 * Calculate billing statistics
 */
export async function getBillingStats(): Promise<BillingStats> {
  // Get all users
  const usersSnapshot = await getDocs(collection(navDb, NAV_USERS_COLLECTION));
  const users = usersSnapshot.docs
    .filter(doc => doc.id !== 'blocked' && doc.id !== 'invites' && doc.id !== 'admin_logs')
    .map(doc => doc.data() as NavUser);

  // Count by status
  const activeSubscribers = users.filter(u => 
    u.subscriptionStatus === 'SUBSCRIBED' || u.subscriptionStatus === 'active'
  ).length;

  const activeTrials = users.filter(u => 
    u.subscriptionStatus === 'TRIAL_ACTIVE' || u.subscriptionStatus === 'trial'
  ).length;

  const expiredUsers = users.filter(u => 
    ['TRIAL_EXPIRED', 'SUBSCRIPTION_EXPIRED', 'expired', 'cancelled'].includes(u.subscriptionStatus)
  ).length;

  // Calculate MRR/ARR
  const mrr = activeSubscribers * SUBSCRIPTION_PRICE;
  const arr = mrr * 12;

  // Get transactions for revenue calculation
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const sixtyDaysAgo = new Date();
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

  const recentTransactions = await getTransactions({
    startDate: sixtyDaysAgo,
    limitCount: 1000
  });

  // Calculate revenue
  const last30DaysRevenue = recentTransactions
    .filter(t => {
      const date = t.timestamp instanceof Timestamp ? t.timestamp.toDate() : new Date(t.timestamp);
      return date >= thirtyDaysAgo && t.status === 'completed' && t.type !== 'refund';
    })
    .reduce((sum, t) => sum + t.amount, 0);

  const prev30DaysRevenue = recentTransactions
    .filter(t => {
      const date = t.timestamp instanceof Timestamp ? t.timestamp.toDate() : new Date(t.timestamp);
      return date >= sixtyDaysAgo && date < thirtyDaysAgo && t.status === 'completed' && t.type !== 'refund';
    })
    .reduce((sum, t) => sum + t.amount, 0);

  // Calculate growth
  const revenueGrowth = prev30DaysRevenue > 0 
    ? ((last30DaysRevenue - prev30DaysRevenue) / prev30DaysRevenue) * 100 
    : 0;

  // Calculate churn (users who cancelled in last 30 days / active users at start)
  const cancellations = recentTransactions.filter(t => 
    t.type === 'cancellation' && 
    (t.timestamp instanceof Timestamp ? t.timestamp.toDate() : new Date(t.timestamp)) >= thirtyDaysAgo
  ).length;
  
  const churnRate = activeSubscribers > 0 
    ? (cancellations / (activeSubscribers + cancellations)) * 100 
    : 0;

  // Conversion rate (trials that converted / total trials completed)
  const conversions = recentTransactions.filter(t => t.type === 'trial_convert').length;
  const trialEnds = activeTrials + conversions + expiredUsers;
  const conversionRate = trialEnds > 0 ? (conversions / trialEnds) * 100 : 0;

  // LTV calculation (ARPU / churn rate)
  const averageRevenuePerUser = activeSubscribers > 0 ? mrr / activeSubscribers : SUBSCRIPTION_PRICE;
  const monthlyChurnRate = churnRate / 100;
  const ltv = monthlyChurnRate > 0 ? averageRevenuePerUser / monthlyChurnRate : averageRevenuePerUser * 12;

  // Failed payments
  const failedPayments = recentTransactions.filter(t => 
    t.status === 'failed' &&
    (t.timestamp instanceof Timestamp ? t.timestamp.toDate() : new Date(t.timestamp)) >= thirtyDaysAgo
  ).length;

  // Total revenue (all time)
  const allTransactions = await getTransactions({ limitCount: 10000 });
  const totalRevenue = allTransactions
    .filter(t => t.status === 'completed' && t.type !== 'refund')
    .reduce((sum, t) => sum + t.amount, 0);

  return {
    mrr,
    arr,
    totalRevenue,
    totalSubscribers: activeSubscribers,
    activeTrials,
    churnRate,
    conversionRate,
    ltv,
    averageRevenuePerUser,
    revenueGrowth,
    failedPayments
  };
}

/**
 * Get revenue breakdown by month
 */
export async function getRevenueByMonth(months: number = 12): Promise<{
  month: string;
  revenue: number;
  subscribers: number;
}[]> {
  const results: { month: string; revenue: number; subscribers: number }[] = [];
  const now = new Date();

  for (let i = months - 1; i >= 0; i--) {
    const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
    const monthName = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

    const transactions = await getTransactions({
      startDate: monthStart,
      endDate: monthEnd
    });

    const revenue = transactions
      .filter(t => t.status === 'completed' && t.type !== 'refund')
      .reduce((sum, t) => sum + t.amount, 0);

    const subscribers = transactions
      .filter(t => t.type === 'purchase' || t.type === 'renewal')
      .length;

    results.push({ month: monthName, revenue, subscribers });
  }

  return results;
}

// ============================================================================
// PROMO CODE MANAGEMENT
// ============================================================================

/**
 * Create a promo code
 */
export async function createPromoCode(
  code: Omit<PromoCode, 'id' | 'usedCount' | 'createdAt'>,
  adminId: string
): Promise<PromoCode> {
  const promoData = {
    ...code,
    code: code.code.toUpperCase(),
    usedCount: 0,
    createdAt: Timestamp.now()
  };

  const docRef = await addDoc(collection(navDb, PROMO_CODES_COLLECTION), promoData);

  await logAdminAction(adminId, 'BULK_ACTION', docRef.id, {
    action: 'create_promo_code',
    code: code.code,
    discountType: code.discountType,
    discountValue: code.discountValue
  });

  return { id: docRef.id, ...promoData };
}

/**
 * Get all promo codes
 */
export async function getPromoCodes(activeOnly: boolean = false): Promise<PromoCode[]> {
  let q = query(
    collection(navDb, PROMO_CODES_COLLECTION),
    orderBy('createdAt', 'desc')
  );

  if (activeOnly) {
    q = query(q, where('active', '==', true));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as PromoCode[];
}

/**
 * Get promo code by code string
 */
export async function getPromoCodeByCode(code: string): Promise<PromoCode | null> {
  const q = query(
    collection(navDb, PROMO_CODES_COLLECTION),
    where('code', '==', code.toUpperCase()),
    limit(1)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as PromoCode;
}

/**
 * Use a promo code
 */
export async function usePromoCode(
  codeId: string,
  userId: string
): Promise<{ success: boolean; message: string }> {
  const codeRef = doc(navDb, PROMO_CODES_COLLECTION, codeId);
  const codeDoc = await getDoc(codeRef);

  if (!codeDoc.exists()) {
    return { success: false, message: 'Promo code not found' };
  }

  const code = codeDoc.data() as PromoCode;

  if (!code.active) {
    return { success: false, message: 'Promo code is not active' };
  }

  const now = new Date();
  if (code.validFrom.toDate() > now) {
    return { success: false, message: 'Promo code is not yet valid' };
  }

  if (code.validUntil.toDate() < now) {
    return { success: false, message: 'Promo code has expired' };
  }

  if (code.maxUses && code.usedCount >= code.maxUses) {
    return { success: false, message: 'Promo code has reached maximum uses' };
  }

  // Increment usage
  await updateDoc(codeRef, {
    usedCount: code.usedCount + 1
  });

  // Record usage
  await addDoc(collection(navDb, PROMO_CODES_COLLECTION, codeId, 'usage'), {
    userId,
    usedAt: Timestamp.now()
  });

  return { success: true, message: 'Promo code applied successfully' };
}

/**
 * Deactivate a promo code
 */
export async function deactivatePromoCode(codeId: string, adminId: string): Promise<void> {
  await updateDoc(doc(navDb, PROMO_CODES_COLLECTION, codeId), {
    active: false
  });

  await logAdminAction(adminId, 'BULK_ACTION', codeId, {
    action: 'deactivate_promo_code'
  });
}

/**
 * Generate multiple promo codes
 */
export async function bulkGeneratePromoCodes(
  count: number,
  template: Omit<PromoCode, 'id' | 'code' | 'usedCount' | 'createdAt'>,
  adminId: string
): Promise<PromoCode[]> {
  const batch = writeBatch(navDb);
  const codes: PromoCode[] = [];

  for (let i = 0; i < count; i++) {
    // Generate unique code
    const code = `${template.description.substring(0, 4).toUpperCase()}${Date.now().toString(36).toUpperCase()}${i.toString(36).toUpperCase()}`.substring(0, 10);
    
    const promoData = {
      ...template,
      code,
      usedCount: 0,
      createdAt: Timestamp.now()
    };

    const docRef = doc(collection(navDb, PROMO_CODES_COLLECTION));
    batch.set(docRef, promoData);
    codes.push({ id: docRef.id, ...promoData });
  }

  await batch.commit();

  await logAdminAction(adminId, 'BULK_ACTION', 'promo_codes', {
    action: 'bulk_generate',
    count,
    description: template.description
  });

  return codes;
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const navigatorXBillingService = {
  // Constants
  SUBSCRIPTION_PRICE,

  // RevenueCat
  fetchRevenueCatCustomer,
  getSubscriptionDetails,
  syncSubscriptionStatus,
  grantPromotionalSubscription,
  requestRefund,

  // Transactions
  recordTransaction,
  getUserTransactions,
  getTransactions,

  // Analytics
  getBillingStats,
  getRevenueByMonth,

  // Promo Codes
  createPromoCode,
  getPromoCodes,
  getPromoCodeByCode,
  usePromoCode,
  deactivatePromoCode,
  bulkGeneratePromoCodes
};

export default navigatorXBillingService;
